import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react'
import useModal from './modalHook';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ITablePaginationData } from '../components/elements/GTable/GTable';
import { getAllBills, getBillById, getBillStatisticsByCompanyId } from '../api/requests';
import useRoute from './routesHook';
import Bill from '../models/Bill';
import useUserAccount from './userAccountHook';
import { formatCurrencyAmount, getFormattedDate } from '../utils/miscFunctions';
import { AlertCircle, Calendar, CheckCircle2, Clock, EyeIcon } from 'lucide-react';
import MarkAsPaidModal from '../views/partners/shared/bills/components/MarkAsPaidModal';

type Props = {
    selectedCompany?: number;
}

interface IBillStatistics {
    overdueAmountDue: number;
    overdueAmountPayable: number;
    overdueBills: number;
    paidAmount: number;
    paidBills: number;
    paymentPercentage: number;
    totalAmountDue: number;
    totalAmountPayable: number;
    totalBills: number;
    unpaidAmountDue: number;
    unpaidAmountPayable: number;
    unpaidBills: number;
}

const useBills = ({ selectedCompany }: Props) => {
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const BillsModalComponent = ModalComponent
    const { billId } = useParams<{ billId?: string }>();
    const [loading, setLoading] = useState<boolean>(true);
    const [billsPaginationData, setBillsPaginationData] = useState<ITablePaginationData>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const [showPerPage, setShowPerPage] = useState<number>(100);
    const navigate = useNavigate();
    const { user } = useUserAccount();
    const queryClient = useQueryClient()

    const fetchAllBills = async () => {
        if (selectedCompany) {

            try {
                const response: any = await getAllBills(selectedCompany, currentPage, showPerPage);

                if (response) {
                    const billsData = response?.data?.content?.map((bill: any) => new Bill(bill)) ?? [];

                    const from = response?.data?.pageable?.offset + 1;
                    const to =
                        from + response?.data?.size > response?.data?.totalElements
                            ? response?.data?.totalElements
                            : from + response?.data?.size - 1;

                    setBillsPaginationData({
                        ...billsPaginationData,
                        per_page: response?.data?.size ?? 0,
                        total: response?.data?.totalElements ?? 0,
                        totalPages: response?.data?.totalPages ?? 0,
                        from,
                        to
                    })

                    return billsData
                }
            } catch (error) { }
            finally {
                setLoading(false);
            }
        }
    }

    const columns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Month',
                        accessor: 'month',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ?? '-'}</span>
                        }
                    },
                    {
                        Header: 'Amount Due',
                        accessor: 'amount',
                        Cell: (row: any) => {
                            return <span>{formatCurrencyAmount(row.cell.value)}</span>
                        }
                    },
                    {
                        Header: 'Amount Payable',
                        accessor: 'amountPayable',
                        Cell: (row: any) => {
                            return <span>{formatCurrencyAmount(row.cell.value)}</span>
                        }
                    },
                    {
                        Header: 'Reference Number',
                        accessor: 'referenceNumber',
                        Cell: (row: any) => {
                            return (
                                <span>{row.cell.value ?? '-'}</span>
                            );
                        }
                    },
                    {
                        Header: 'Discount',
                        accessor: 'discount',
                        Cell: (row: any) => {
                            return (
                                <span>{formatCurrencyAmount(row.cell.value)}</span>
                            );
                        }
                    },
                    ...(user?.isInternalAdmin() ? [
                        {
                            Header: 'Broker',
                            accessor: 'broker',
                            Cell: (row: any) => {
                                return <span>{row.cell.value?.name ?? '-'}</span>
                            }
                        }
                    ] : []),
                    {
                        Header: 'Due Date',
                        accessor: 'dueDate',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Action',
                        id: 'Action',
                        Cell: (row: any) => {
                            return (
                                <EyeIcon className="h-4 w-4 cursor-pointer hover:text-blue-500" onClick={() => navigate(`/bills/${row.row.original.id}`)} />
                            );
                        }
                    }
                ]
            }
        ],
        []
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    const fetchBillStatisticsByCompanyId = async () => {
        if (!selectedCompany) return;
        try {
            const response: any = await getBillStatisticsByCompanyId(selectedCompany);
            if (response) {
                const statistics: IBillStatistics = response?.data;

                return {
                    overdueBills: statistics?.overdueBills,
                    overdueAmountDue: statistics?.overdueAmountDue,
                    overdueAmountPayable: statistics?.overdueAmountPayable,
                    overdueDiscount: statistics?.overdueAmountPayable - statistics?.overdueAmountDue,

                    paidAmount: statistics?.paidAmount,
                    paidBills: statistics?.paidBills,
                    paidDiscount: 0,
                    paymentPercentage: statistics?.paymentPercentage,

                    totalBills: statistics?.totalBills,
                    totalAmountDue: statistics?.totalAmountDue,
                    totalAmountPayable: statistics?.totalAmountPayable,
                    totalDiscount: statistics?.totalAmountPayable - statistics?.totalAmountDue,
                    totalDue: statistics?.totalAmountDue,

                    unpaidAmountDue: statistics?.unpaidAmountDue,
                    unpaidAmountPayable: statistics?.unpaidAmountPayable,
                    unpaidBills: statistics?.unpaidBills,
                    unpaidDiscount: statistics?.unpaidAmountPayable - statistics?.unpaidAmountDue,
                    unpaidTotalAmount: statistics?.unpaidBills,
                };

            }
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    // Bill status badge component
    const StatusBadge = ({ status }: { status: string | boolean }) => {
        // Map status to consistent string values
        const normalizedStatus = typeof status === 'boolean'
            ? (status ? 'paid' : 'overdue')
            : status.toLowerCase();

        // Get display text for status
        const getStatus = () => {
            switch (normalizedStatus) {
                case 'paid':
                    return "Paid";
                case 'overdue':
                    return "Overdue";
                case 'pending':
                    return "Pending";
                default:
                    return "Pending";
            }
        };

        // Get styling based on status
        const getStatusStyle = () => {
            switch (normalizedStatus) {
                case 'paid':
                    return { color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
                case 'overdue':
                    return { color: 'bg-red-100 text-red-800', icon: AlertCircle };
                case 'pending':
                    return { color: 'bg-yellow-100 text-yellow-800', icon: Clock };
                default:
                    return { color: 'bg-gray-100 text-gray-800', icon: Clock };
            }
        };


        const { color, icon: Icon } = getStatusStyle();

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${color}`}>
                <Icon className="w-3 h-3 mr-1" />
                <span className="capitalize">{getStatus()}</span>
            </span>
        );
    };

    const fetchBillDetail = async () => {
        try {
            const response: any = await getBillById(Number(billId));

            if (response) {
                return new Bill(response?.data);
            }
        } catch (error) {
            console.error('Error fetching bill detail:', error);
        }
    };

    const handleMarkAsPaid = (billDetail: Bill,) => {
        updateModalStates({
            show: true,
            children: (
                <MarkAsPaidModal
                    bill={billDetail}
                    onProceed={() => {
                        closeModal();
                        queryClient.invalidateQueries({ queryKey: ['fetchBillDetail'] });
                    }}
                    onCancel={closeModal}
                />
            ),
            size: 'lg'
        });
    };

    return {
        columns,
        billsPaginationData,
        loading,
        handlePageChange,
        showPerPage,
        setShowPerPage,
        BillsModalComponent,
        StatusBadge,
        currentPage,
        fetchAllBills,
        fetchBillStatisticsByCompanyId,
        handleMarkAsPaid,
        fetchBillDetail
    }
}

export default useBills